/**
 * Gestione "Ragioni sociali destinatarie" e "Destinazioni di consegna"
 * usate negli ordini fornitore (compaiono nel PDF dell'ordine).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Building2, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_PROFILE = { business_name: "", vat_number: "", address: "", city: "", cap: "", province: "", phone: "", email: "", contact_name: "" };
const EMPTY_LOCATION = { label: "", address: "", city: "", cap: "", province: "", phone: "", email: "", contact_name: "", receiving_hours: "", notes: "" };

const Field = ({ label, value, onChange, testid, span }) => {
  const uniqueId = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  return (
  <div className={span ? "sm:col-span-2" : ""}>
    <Label className="text-xs">{label}</Label>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 bg-white border-stone-300" data-testid={testid || `recipient-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${uniqueId}`} />
  </div>
  );
};

const ProfileForm = ({ form, setForm }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
    <Field span label="Ragione sociale *" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} testid="profile-business-name" />
    <Field label="P. IVA" value={form.vat_number} onChange={(v) => setForm({ ...form, vat_number: v })} />
    <Field label="Referente" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
    <Field label="Indirizzo" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
    <Field label="Città" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
    <Field label="CAP" value={form.cap} onChange={(v) => setForm({ ...form, cap: v })} />
    <Field label="Provincia" value={form.province} onChange={(v) => setForm({ ...form, province: v })} />
    <Field label="Telefono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
    <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
  </div>
);

const LocationForm = ({ form, setForm }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
    <Field span label="Nome destinazione * (es. Magazzino Firenze)" value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="location-label" />
    <Field span label="Indirizzo *" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="location-address" />
    <Field label="Città" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
    <Field label="CAP" value={form.cap} onChange={(v) => setForm({ ...form, cap: v })} />
    <Field label="Provincia" value={form.province} onChange={(v) => setForm({ ...form, province: v })} />
    <Field label="Referente" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
    <Field label="Telefono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
    <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
    <Field span label="Orari di ricevimento (es. Lun-Ven 6:00-12:00)" value={form.receiving_hours} onChange={(v) => setForm({ ...form, receiving_hours: v })} testid="location-hours" />
    <Field span label="Note" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
  </div>
);

const Section = ({ icon: Icon, title, items, renderItem, onNew, newLabel, testid }) => (
  <div className="border border-stone-200 rounded-lg overflow-hidden">
    <div className="bg-stone-50 px-3 py-2 flex items-center justify-between">
      <span className="text-sm font-semibold text-stone-800 flex items-center gap-1.5"><Icon className="w-4 h-4 text-[#8A1C2A]" /> {title} ({items.length})</span>
      <Button size="sm" onClick={onNew} className="h-7 text-xs bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid={testid}>
        <Plus className="w-3 h-3 mr-1" /> {newLabel}
      </Button>
    </div>
    <div className="divide-y divide-stone-100 max-h-56 overflow-y-auto">
      {items.length === 0 ? <p className="p-3 text-xs text-stone-500 italic">Nessun elemento — creane uno per farlo comparire nel PDF ordine.</p> : items.map(renderItem)}
    </div>
  </div>
);

export const RecipientsManagerDialog = ({ open, onClose, onChanged }) => {
  const [profiles, setProfiles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editProfile, setEditProfile] = useState(null);   // {id?} | null
  const [editLocation, setEditLocation] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, l] = await Promise.all([axios.get(`${API}/company-profiles`), axios.get(`${API}/delivery-locations`)]);
      setProfiles(p.data || []);
      setLocations(l.data || []);
    } catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const saveProfile = async () => {
    if (!editProfile.business_name) { toast.error("Ragione sociale obbligatoria"); return; }
    setSaving(true);
    try {
      if (editProfile.id) await axios.put(`${API}/company-profiles/${editProfile.id}`, editProfile);
      else await axios.post(`${API}/company-profiles`, editProfile);
      toast.success("Ragione sociale salvata");
      setEditProfile(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const saveLocation = async () => {
    if (!editLocation.label || !editLocation.address) { toast.error("Nome e indirizzo obbligatori"); return; }
    setSaving(true);
    try {
      if (editLocation.id) await axios.put(`${API}/delivery-locations/${editLocation.id}`, editLocation);
      else await axios.post(`${API}/delivery-locations`, editLocation);
      toast.success("Destinazione salvata");
      setEditLocation(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const delProfile = async (p) => {
    if (!window.confirm(`Eliminare "${p.business_name}"?`)) return;
    try { await axios.delete(`${API}/company-profiles/${p.id}`); toast.success("Eliminata"); load(); onChanged?.(); }
    catch { toast.error("Errore"); }
  };
  const delLocation = async (l) => {
    if (!window.confirm(`Eliminare "${l.label}"?`)) return;
    try { await axios.delete(`${API}/delivery-locations/${l.id}`); toast.success("Eliminata"); load(); onChanged?.(); }
    catch { toast.error("Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="acq-dialog max-w-3xl bg-white max-h-[92vh] overflow-y-auto" data-testid="recipients-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#8A1C2A]" /> Destinatari &amp; Consegne (ordini fornitore)</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Le ragioni sociali e le destinazioni salvate qui sono selezionabili negli ordini fornitore e compaiono automaticamente nel PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Section icon={Building2} title="Ragioni sociali destinatarie" items={profiles} onNew={() => setEditProfile({ ...EMPTY_PROFILE })} newLabel="Nuova" testid="new-profile-btn"
            renderItem={(p) => (
              <div key={p.id} className="p-2.5 flex items-start justify-between gap-2 text-sm" data-testid={`profile-row-${p.id}`}>
                <div>
                  <p className="font-medium">{p.business_name}</p>
                  <p className="text-xs text-stone-500">{[p.vat_number && `P.IVA ${p.vat_number}`, p.address, p.city].filter(Boolean).join(" · ")}</p>
                  {(p.contact_name || p.phone || p.email) && <p className="text-xs text-stone-500">{[p.contact_name, p.phone, p.email].filter(Boolean).join(" · ")}</p>}
                </div>
                <span className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-9 w-9" title="Modifica intestatario" data-testid={`recipient-profile-edit-${p.id}`} onClick={() => setEditProfile({ ...EMPTY_PROFILE, ...p })}><Edit className="w-3.5 h-3.5 text-stone-500" /></Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9" title="Archivia intestatario" data-testid={`recipient-profile-delete-${p.id}`} onClick={() => delProfile(p)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </span>
              </div>
            )} />

          <Section icon={MapPin} title="Destinazioni di consegna" items={locations} onNew={() => setEditLocation({ ...EMPTY_LOCATION })} newLabel="Nuova" testid="new-location-btn"
            renderItem={(l) => (
              <div key={l.id} className="p-2.5 flex items-start justify-between gap-2 text-sm" data-testid={`location-row-${l.id}`}>
                <div>
                  <p className="font-medium">{l.label}</p>
                  <p className="text-xs text-stone-500">{[l.address, l.cap, l.city, l.province].filter(Boolean).join(", ")}</p>
                  {(l.contact_name || l.phone) && <p className="text-xs text-stone-500">{[l.contact_name, l.phone, l.email].filter(Boolean).join(" · ")}</p>}
                  {l.receiving_hours && <p className="text-xs text-amber-700 font-medium">🕒 {l.receiving_hours}</p>}
                </div>
                <span className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-9 w-9" title="Modifica destinazione" data-testid={`recipient-location-edit-${l.id}`} onClick={() => setEditLocation({ ...EMPTY_LOCATION, ...l })}><Edit className="w-3.5 h-3.5 text-stone-500" /></Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9" title="Archivia destinazione" data-testid={`recipient-location-delete-${l.id}`} onClick={() => delLocation(l)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </span>
              </div>
            )} />
        </div>

        <DialogFooter><Button onClick={onClose} data-testid="recipients-manager-close-btn" className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white">Chiudi</Button></DialogFooter>

        {editProfile && (
          <Dialog open={true} onOpenChange={() => setEditProfile(null)}>
            <DialogContent className="acq-dialog max-w-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="profile-form-dialog">
              <DialogHeader><DialogTitle>{editProfile.id ? "Modifica ragione sociale" : "Nuova ragione sociale"}</DialogTitle></DialogHeader>
              <ProfileForm form={editProfile} setForm={setEditProfile} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditProfile(null)} data-testid="recipient-profile-cancel" className="border-stone-300">Annulla</Button>
                <Button onClick={saveProfile} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="save-profile-btn">{saving ? "..." : "Salva"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {editLocation && (
          <Dialog open={true} onOpenChange={() => setEditLocation(null)}>
            <DialogContent className="acq-dialog max-w-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="location-form-dialog">
              <DialogHeader><DialogTitle>{editLocation.id ? "Modifica destinazione" : "Nuova destinazione di consegna"}</DialogTitle></DialogHeader>
              <LocationForm form={editLocation} setForm={setEditLocation} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditLocation(null)} data-testid="recipient-location-cancel" className="border-stone-300">Annulla</Button>
                <Button onClick={saveLocation} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="save-location-btn">{saving ? "..." : "Salva"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecipientsManagerDialog;
